import { type AlgoliaRecord, type BestOffer } from '../../../algolia'
import {
  GTM_EVENT_CLICK_PRODUCT,
  GTM_EVENT_CLICK_PRODUCT_OFFERS,
} from '../../../gtm'
import { queryStringEncode, urlGetRelativeUrl } from '../../../url-handling'

import { productSearchAlgoliaAccessorGetVariantsData } from './productSearchAlgoliaAccessorGetVariantsData'

interface ProductSearchAlgoliaAccessorGetOffersData {
  bestOffer: BestOffer | undefined
  isSaeOffer: boolean
  productHasMultipleOffers: boolean
  productUrl?: string
  shouldDisplayLinkToOfferPage: boolean
}

const OFFER_TYPE_SAE = 'SAE'

const productIsSaeOffer = (
  bestOffer: BestOffer | undefined,
  isMarketplace: boolean,
): boolean => {
  return Boolean(
    !isMarketplace || (bestOffer && bestOffer.type === OFFER_TYPE_SAE),
  )
}

type QueryParams = {
  eventName?: string
  offerSelectionDrawer?: boolean
}

type ProductOrOffersUrlParams = {
  deeplink?: string
  eventName?: string
  shouldDisplayLinkToOfferPage?: boolean
}
/*
  This function appends a query parameters that triggers an automatic opening of the PDP Offers Drawer which contains all the offers of the product.
 */
const getProductOrOffersUrl = ({
  deeplink,
  shouldDisplayLinkToOfferPage,
}: ProductOrOffersUrlParams): string => {
  const queryParams: QueryParams = {}

  if (!deeplink) {
    return ''
  }

  queryParams.eventName = decodeURIComponent(GTM_EVENT_CLICK_PRODUCT)

  // TO DO/INVESTIGATE IF THIS IMPLEMENTATION IS ENOUGH (IN GTM TICKETS: GTM_EVENT_CLICK_PRODUCT)
  const productPageUrl = urlGetRelativeUrl(deeplink)

  if (!shouldDisplayLinkToOfferPage) {
    return `${productPageUrl}${queryStringEncode(queryParams)}`
  }

  // if the product has offers then we return the product page url that will access the PDP with the right-side drawer opened
  // TO DO/INVESTIGATE IF THIS IMPLEMENTATION IS ENOUGH (IN GTM TICKETS: GTM_EVENT_CLICK_PRODUCT_OFFERS)
  queryParams.offerSelectionDrawer = true
  queryParams.eventName = decodeURIComponent(GTM_EVENT_CLICK_PRODUCT_OFFERS)

  return `${productPageUrl}${queryStringEncode(queryParams)}`
}

/*
  This accessor is responsible for maintaining all the logic to determine if we have bestOffers, if the offer is an SAE type and so on.
  Any additional logic related to the offers should be within this accessor or coming into it via imports.
  If we need more conditional logic on the UI then we will use export flags from the accessor and avoid too much logic on the actual offer data in the UI.
 */
export const productSearchAlgoliaAccessorGetOffersData = (
  algoliaRecord: AlgoliaRecord,
  isMarketplace: boolean,
): ProductSearchAlgoliaAccessorGetOffersData => {
  const {
    best_offer: bestOffer,
    deeplink,
    offers_count: offersCount,
  } = algoliaRecord

  const productHasMultipleOffers = offersCount > 1
  const isSaeOffer = productIsSaeOffer(bestOffer, isMarketplace)

  const { productHasMultipleVariants } =
    productSearchAlgoliaAccessorGetVariantsData(algoliaRecord)

  const shouldDisplayLinkToOfferPage =
    productHasMultipleOffers && isMarketplace && !productHasMultipleVariants

  return {
    bestOffer,
    isSaeOffer,
    productHasMultipleOffers,
    productUrl: getProductOrOffersUrl({
      deeplink,
      shouldDisplayLinkToOfferPage,
    }),
    shouldDisplayLinkToOfferPage,
  }
}
